import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { BarChart2, LineChart, Home } from "lucide-react";

const Tabs = ({ isSidebarOpen }) => {
  const [activeTab, setActiveTab] = useState("Overview");
  const [isMobile, setIsMobile] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const selectedTeam = localStorage.getItem("selectedTeam") || "";

  // Simple Project Nova Design System style
  const styles = {
    primaryColor: "#1d0c46", // Deep Indigo
    secondaryColor: "#caa968", // Muted Gold
    backgroundColor: "#f8fafc", // Light gray background
    textColor: "#0f172a",
    textHeaders: "#1d0c46", // Deep Indigo for headers
    borderColor: "rgba(255, 255, 255, 0.18)", // Glass border
    glassEffect: "rgba(255, 255, 255, 0.7)",
    glassBorder: "rgba(255, 255, 255, 0.18)",
    cardShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
    cardShadowHover: "0 4px 16px rgba(0, 0, 0, 0.1)"
  };

  const tabs = useMemo(() => ([
    { 
      name: "Offchain Analytics", 
      primary: "offchain",
      icon: <BarChart2 size={isMobile ? 16 : 18} />
    },
    { 
      name: "Onchain Explorer", 
      primary: "onchain",
      icon: <LineChart size={isMobile ? 16 : 18} />
    },
    { 
      name: "GeoInsights", 
      path: "dashboard",
      icon: <Home size={isMobile ? 16 : 18} />
    }
  ]), [isMobile]);

  // Check if mobile view
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    // Initial check
    checkMobile();
    
    // Listen for window resize
    window.addEventListener('resize', checkMobile);
    
    // Cleanup
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Set active tab based on current route
  useEffect(() => {
    const segments = location.pathname.split('/').filter(Boolean);
    const lastSegment = segments[segments.length - 1] || '';
    let currentTab = tabs[0];

    if (lastSegment === 'overview') {
      const params = new URLSearchParams(location.search);
      const primary = params.get('primary') || 'offchain';
      currentTab = tabs.find(tab => tab.primary === primary) || tabs[0];
    } else {
      const matched = tabs.find(tab => tab.path === lastSegment);
      if (matched) {
        currentTab = matched;
      }
    }

    setActiveTab(currentTab.name);
  }, [location, tabs]);

  if (isSidebarOpen) return null; // Hide tabs when sidebar is open

  const handleTabClick = (tab) => {
    setActiveTab(tab.name);
    if (tab.primary) {
      navigate(`/${selectedTeam}/overview?primary=${tab.primary}`);
    } else if (tab.path) {
      navigate(`/${selectedTeam}/${tab.path}`);
    }
  };

  return (
    <div className="w-full overflow-x-auto mb-4">
      <div 
        className="glass-effect rounded-[12px] p-2 flex -mt-4 relative z-10"
        style={{ fontFamily: 'Raleway, sans-serif', background: 'rgba(255, 255, 255, 0.25)', backdropFilter: 'blur(20px)' }}
      >
        {tabs.map((tab) => (
          <button
            key={tab.name}
            onClick={() => handleTabClick(tab)}
            className={`relative flex items-center gap-2 px-4 py-2 text-sm font-medium transition-all duration-200 rounded-[8px] flex-1 justify-center`}
            style={{ 
              color: activeTab === tab.name ? 'white' : styles.textColor,
              backgroundColor: activeTab === tab.name ? styles.primaryColor : 'transparent',
              fontFamily: 'Raleway, sans-serif'
            }}
          >
            {tab.icon}
            {!isMobile && <span>{tab.name}</span>}
            {isMobile && activeTab === tab.name && <span>{tab.name}</span>}
          </button>
        ))}
      </div>
    </div>
  );
};

export default Tabs;
